/**
 * PreviewPane — sticky bottom bar that surfaces the English readout of the
 * step the user is currently editing.
 *
 * The pane renders something like:
 *
 *   When Outstanding conditions is greater than 1, send Email + WhatsApp
 *   to Title company.
 *
 * Pure projection of the builder state through @mana/shared/preview helpers,
 * so the wording stays in sync with API-side renderers (audit log, etc.).
 */

import { useMemo } from 'react';
import { describeActionInEnglish, describeConditionInEnglish } from '@mana/shared';
import type { VisualConditionGroup, VisualStep } from './serialize.js';
import { actionRowToSpecs, rowsToGuard } from './serialize.js';

export interface PreviewPaneProps {
  step: VisualStep | null;
}

export function PreviewPane({ step }: PreviewPaneProps) {
  const sentence = useMemo(() => composeSentence(step), [step]);

  if (!step) {
    return (
      <footer
        className="sticky bottom-0 border-t border-slate-200 bg-white/95 px-4 py-2 text-[11px] text-slate-500 backdrop-blur"
        aria-live="polite"
      >
        Pick a step or build one to preview the workflow sentence here.
      </footer>
    );
  }

  return (
    <footer
      className="sticky bottom-0 border-t border-brand-navy-200 bg-brand-navy-50 px-4 py-2 text-[12px] text-brand-navy-800 backdrop-blur"
      aria-live="polite"
    >
      <span className="font-semibold uppercase tracking-wider text-brand-navy-700">
        Preview
      </span>{' '}
      <span className="font-display italic">{sentence}</span>
    </footer>
  );
}

function composeSentence(step: VisualStep | null): string {
  if (!step) return '';
  const parts: string[] = [];

  // WHEN clause
  if (step.unsupportedGuard) {
    parts.push(
      step.originalGuard
        ? `When ${describeConditionInEnglish(step.originalGuard)},`
        : 'At every step entry,',
    );
  } else {
    const when = describeGroups(step.conditionGroups, step.groupLogic);
    parts.push(when ? `When ${when},` : 'At every step entry,');
  }

  // THEN clause
  const allSpecs = step.actionRows.flatMap(actionRowToSpecs);
  if (allSpecs.length === 0) {
    parts.push('do nothing (no actions configured).');
  } else {
    parts.push(allSpecs.map(describeActionInEnglish).join('; ') + '.');
  }

  return parts.join(' ');
}

/**
 * Render the two-level groups in English, parenthesizing multi-row groups when
 * there is more than one group so the reader can tell the precedence:
 *   (A AND B) OR (C)
 */
function describeGroups(
  groups: readonly VisualConditionGroup[],
  groupLogic: 'and' | 'or',
): string {
  const pieces: string[] = [];
  for (const g of groups) {
    const guard = rowsToGuard(g.rows, g.logic);
    if (!guard) continue; // empty group
    const text = describeConditionInEnglish(guard);
    // Parenthesize multi-row groups when they'll sit alongside other groups.
    pieces.push(g.rows.length > 1 ? `(${text})` : text);
  }
  if (pieces.length === 0) return '';
  if (pieces.length === 1) {
    // Single group — strip the wrapping parens we may have added.
    return pieces[0]!.replace(/^\((.*)\)$/, '$1');
  }
  return pieces.join(` ${groupLogic.toUpperCase()} `);
}
